// Kennungen: UUID v7 (zeitlich sortiert) – in der Datenbank per `uuidv7()`, im Client für
// Idempotenzschlüssel und vorab vergebene IDs per `newId()`.
import { z } from 'zod'

export type Id = string
export type TenantId = string
export type UserId = string
export type LocationId = string

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isUuid = (value: unknown): value is Id => typeof value === 'string' && UUID_PATTERN.test(value)

/**
 * Zod-Schema für IDs in Verträgen. Bewusst `z.guid()` (nur Form 8-4-4-4-12) statt `z.uuid()`:
 * Zod 4 prüft bei `z.uuid()` Versions- und Variantenbits und lehnt feste Demo-/Test-IDs ab.
 */
export const zId = z.guid()

interface CryptoLike {
  getRandomValues<T extends Uint8Array>(array: T): T
}

function randomBytes(length: number): Uint8Array {
  const crypto = (globalThis as { crypto?: CryptoLike }).crypto
  if (!crypto) throw new Error('Web Crypto ist nicht verfügbar.')
  return crypto.getRandomValues(new Uint8Array(length))
}

let lastMs = 0
let sequence = 0

/**
 * UUID v7 nach RFC 9562: 48 Bit Unix-Millisekunden, 12 Bit Zähler, 62 Bit Zufall.
 * Monoton je Prozess: Springt die Uhr zurück (oder ist `now` älter als die letzte ID), läuft der Zähler
 * auf dem letzten Zeitstempel weiter – IDs sortieren immer in Erzeugungsreihenfolge.
 */
export function newId(now: number = Date.now()): Id {
  if (now > lastMs) {
    lastMs = now
    sequence = (randomBytes(2)[0] as number) & 0x7f // kleiner Startwert lässt Platz zum Hochzählen
  } else {
    sequence += 1
    if (sequence > 0xfff) {
      lastMs += 1
      sequence = 0
    }
  }
  const bytes = randomBytes(16)
  const ms = lastMs
  bytes[0] = Math.floor(ms / 2 ** 40) & 0xff
  bytes[1] = Math.floor(ms / 2 ** 32) & 0xff
  bytes[2] = Math.floor(ms / 2 ** 24) & 0xff
  bytes[3] = Math.floor(ms / 2 ** 16) & 0xff
  bytes[4] = Math.floor(ms / 2 ** 8) & 0xff
  bytes[5] = ms & 0xff
  bytes[6] = 0x70 | ((sequence >> 8) & 0x0f)
  bytes[7] = sequence & 0xff
  bytes[8] = 0x80 | ((bytes[8] as number) & 0x3f)
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Zeitstempel (ms) aus einer UUID v7; `null`, wenn es keine v7 ist. */
export function idTimestamp(id: Id): number | null {
  if (!isUuid(id) || id[14] !== '7') return null
  return parseInt(id.slice(0, 8) + id.slice(9, 13), 16)
}

/** Idempotenzschlüssel für eine Nutzeraktion (ein Schlüssel je Absicht, bei Wiederholung derselbe). */
export const newIdempotencyKey = (): string => newId()

/** Kurze Anzeigeform für Protokolle und Fehlermeldungen: die letzten 8 Zeichen. */
export const shortId = (id: Id): string => id.slice(-8)
